import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { GovMediaFilesForQuestionsService } from '../../../../api/api-services/gov-media-files-for-questions.service';
import { IGovMediaImportStatusVm } from '../../../../api/gov-media-import-status-vm.model';

/**
 * The "Media" tab of the question base: upload of the ministry media package (ZIP with the films and
 * photos of the questions). The archive is streamed to the API, which unpacks it in the background;
 * the tab follows the import and shows what was added, replaced, left unchanged or skipped.
 */
@Component({
  selector: 'app-media-import',
  standalone: true,
  imports: [CommonModule, ButtonModule, ProgressBarModule, PanelModule, TooltipModule],
  templateUrl: './media-import.component.html',
  styleUrl: './media-import.component.scss',
})
export class MediaImportComponent implements OnInit {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  private readonly _service = inject(GovMediaFilesForQuestionsService);
  private readonly _messageService = inject(MessageService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);

  /** How often the running import is asked for its progress. */
  readonly pollIntervalMs = 2000;

  readonly status = signal<IGovMediaImportStatusVm | null>(null);
  readonly selectedFile = signal<File | null>(null);
  readonly uploading = signal(false);
  /** 0-100 while the archive is being sent; null otherwise. */
  readonly uploadProgress = signal<number | null>(null);
  readonly uploadedBytes = signal(0);
  readonly loadingStatus = signal(false);
  readonly error = signal<string | null>(null);

  private pollSubscription?: Subscription;
  private uploadSubscription?: Subscription;

  ngOnInit(): void {
    this.loadStatus();
  }

  get isRunning(): boolean {
    return !!this.status()?.isRunning;
  }

  get canUpload(): boolean {
    return !!this.selectedFile() && !this.uploading() && !this.isRunning;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    this.error.set(null);
    if (!file) {
      this.selectedFile.set(null);
      return;
    }
    if (!/\.zip$/i.test(file.name)) {
      this.selectedFile.set(null);
      input.value = '';
      this.error.set('Wybierz plik ZIP z paczką mediów (filmy i zdjęcia z ministerstwa).');
      return;
    }
    this.selectedFile.set(file);
  }

  clearFile(): void {
    this.selectedFile.set(null);
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  upload(): void {
    const file = this.selectedFile();
    if (!file || !this.canUpload) {
      return;
    }
    this.error.set(null);
    this.uploading.set(true);
    this.uploadProgress.set(0);
    this.uploadedBytes.set(0);

    this.uploadSubscription?.unsubscribe();
    this.uploadSubscription = this._service
      .upload(file)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.uploadedBytes.set(event.loaded);
            const total = event.total ?? file.size;
            this.uploadProgress.set(total > 0 ? Math.min(100, Math.round((event.loaded / total) * 100)) : 0);
          } else if (event.type === HttpEventType.Response) {
            this.uploading.set(false);
            this.uploadProgress.set(null);
            this.clearFile();
            if (event.body) {
              this.status.set(event.body);
            }
            this._messageService.add({
              severity: 'info',
              summary: 'Paczka przesłana',
              detail: 'Plik jest na serwerze. Rozpakowywanie i indeksowanie trwa w tle.',
            });
            this.startPolling();
          }
          this._cdr.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.uploading.set(false);
          this.uploadProgress.set(null);
          this.error.set(this.readError(err, 'Nie udało się przesłać paczki mediów.'));
          this._cdr.detectChanges();
          // The server may have refused because an import is running - show that import.
          this.loadStatus();
        },
      });
  }

  cancelUpload(): void {
    this.uploadSubscription?.unsubscribe();
    this.uploadSubscription = undefined;
    this.uploading.set(false);
    this.uploadProgress.set(null);
    this._messageService.add({ severity: 'warn', summary: 'Przesyłanie przerwane', detail: 'Paczka nie została wysłana.' });
  }

  loadStatus(): void {
    this.loadingStatus.set(true);
    this._service
      .get()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (status) => {
          this.loadingStatus.set(false);
          this.applyStatus(status);
          if (status.isRunning) {
            this.startPolling();
          }
          this._cdr.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.loadingStatus.set(false);
          this.error.set(this.readError(err, 'Nie udało się pobrać stanu importu mediów.'));
          this._cdr.detectChanges();
        },
      });
  }

  private startPolling(): void {
    if (this.pollSubscription) {
      return;
    }
    this.pollSubscription = timer(this.pollIntervalMs, this.pollIntervalMs)
      .pipe(
        switchMap(() => this._service.get()),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (status) => {
          this.applyStatus(status);
          this._cdr.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.stopPolling();
          this.error.set(this.readError(err, 'Utracono łączność z serwerem podczas importu. Kliknij „Odśwież”.'));
          this._cdr.detectChanges();
        },
      });
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = undefined;
  }

  private applyStatus(status: IGovMediaImportStatusVm): void {
    const previous = this.status();
    this.status.set(status);
    if (!status.isRunning) {
      this.stopPolling();
      // Announce the end once: the status we followed was running a moment ago.
      if (previous?.isRunning && previous.jobId === status.jobId) {
        if (status.state === 'Completed') {
          this._messageService.add({
            severity: status.failedCount > 0 ? 'warn' : 'success',
            summary: 'Import mediów zakończony',
            detail: `Dodane: ${status.addedCount}, zastąpione: ${status.replacedCount}, bez zmian: ${status.unchangedCount}, pominięte: ${status.skippedCount}, błędy: ${status.failedCount}.`,
            life: 10000,
          });
        } else if (status.state === 'Failed') {
          this._messageService.add({
            severity: 'error',
            summary: 'Import mediów nie powiódł się',
            detail: status.errorMessage ?? 'Szczegóły w logu serwera.',
            life: 10000,
          });
        }
      }
    }
  }

  stateLabel(status: IGovMediaImportStatusVm | null): string {
    switch (status?.state) {
      case 'Extracting':
        return 'Rozpakowywanie paczki…';
      case 'Indexing':
        return 'Odświeżanie indeksu mediów…';
      case 'Completed':
        return status.failedCount > 0 ? 'Zakończony z błędami' : 'Zakończony';
      case 'Failed':
        return 'Nie powiódł się';
      default:
        return 'Brak importu od uruchomienia serwera';
    }
  }

  stateClass(status: IGovMediaImportStatusVm | null): string {
    switch (status?.state) {
      case 'Extracting':
      case 'Indexing':
        return 'state-badge--running';
      case 'Completed':
        return status.failedCount > 0 ? 'state-badge--warning' : 'state-badge--completed';
      case 'Failed':
        return 'state-badge--failed';
      default:
        return 'state-badge--idle';
    }
  }

  formatBytes(bytes: number | null | undefined): string {
    if (!bytes || bytes <= 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit++;
    }
    return `${value.toLocaleString('pl-PL', { maximumFractionDigits: unit >= 2 ? 2 : 0 })} ${units[unit]}`;
  }

  /** UTC from the API (no zone in the value) shown in the operator's local time. */
  formatDateTime(input: string | null | undefined): string {
    if (!input) {
      return '';
    }
    const date = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(input) ? input : `${input}Z`);
    return date.toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  private readError(err: HttpErrorResponse, fallback: string): string {
    if (err.status === 0) {
      return 'Połączenie z serwerem zostało przerwane. Sprawdź sieć i spróbuj ponownie.';
    }
    if (err.status === 413) {
      return 'Serwer odrzucił plik jako za duży (limit rozmiaru żądania na serwerze WWW).';
    }
    const body = err.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    return body?.message || body?.Message || body?.errorMessages?.[0] || fallback;
  }
}
